'use client';

import { useEffect } from 'react';
import { usePathname } from 'core/i18n/navigation';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { isProtectedPath } from 'config';
import {
  isSegmentImplemented,
  loadSegment,
  reportPageView,
} from 'utils/analytics/segment';
import { captureUtmParams } from 'utils/analytics/utm';

// How long React Query must have no request in flight before a signed-in
// page counts as loaded.
export const QUIET_MS = 300;
// Load Segment after this long whatever is still in flight.
export const MAX_WAIT_MS = 4000;

// The client side of Segment: loads the script and reports page views.
//
// Loading. Public pages fetch the script straight away. Signed-in pages
// wait until their data has arrived, so the script and the destinations it
// pulls in (GA4, Meta Pixel, Hotjar) don't compete with the requests the
// user is waiting on. "Data has arrived" is read from React Query: no query
// has fetched for a short while. Calls made in the meantime are queued by
// the stub in SegmentTag and sent once the script is up, so nothing is lost.
//
// Page views. The App Router has no `router.events`, so a page view is
// reported on every pathname change. The mount run covers the initial load,
// which is why the snippet in SegmentTag has no `analytics.page()` of its
// own: that would double-count `page_view` in GA4. UTMs are read from the
// live URL inside captureUtmParams, so useSearchParams isn't needed (it
// would opt static pages into client rendering).
export const SegmentAnalytics = (): null => {
  const pathname = usePathname();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isSegmentImplemented()) return;
    if (!isProtectedPath(pathname)) {
      loadSegment();
      return;
    }
    return loadWhenQuiet(queryClient);
    // Decided once per mount; loadSegment only ever loads once anyway.
  }, []);

  useEffect(() => {
    if (!isSegmentImplemented()) return;
    captureUtmParams();
    reportPageView();
  }, [pathname]);

  return null;
};

// Load once React Query has been idle for QUIET_MS, or after MAX_WAIT_MS at
// the latest. Returns the cleanup for the effect.
const loadWhenQuiet = (queryClient: QueryClient): (() => void) => {
  let quiet: ReturnType<typeof setTimeout> | undefined;

  // Every cache event restarts the quiet period if nothing is in flight.
  const arm = () => {
    clearTimeout(quiet);
    if (queryClient.isFetching() === 0) quiet = setTimeout(done, QUIET_MS);
  };

  const unsubscribe = queryClient.getQueryCache().subscribe(arm);
  const cap = setTimeout(done, MAX_WAIT_MS);
  arm();

  function done() {
    cleanup();
    loadSegment();
  }

  function cleanup() {
    unsubscribe();
    clearTimeout(quiet);
    clearTimeout(cap);
  }

  return cleanup;
};
